import { ref, unref, watch } from 'vue';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { $log } from '@/common/api/log.service';

const ENCOUNTER_COUNT = 50;
const OPEN_STATUSES = ['in-progress', 'onleave'];

/**
 * Most recently started first, ties broken by id.
 *
 * A patient can be in more than one program at once. Only one episode is ever active, so every
 * candidate carries the same one and the episode is not what is at stake - which PROGRAM
 * ENCOUNTER answers is. That encounter is what a schedule is read against here and what a dose
 * records as its context, so an arbitrary pick attributes the work to an arbitrary program.
 *
 * Exported because the medication pass answers the same question on its own read, and two
 * spellings of "most recent" would eventually disagree.
 */
export const byMostRecentThenId = (a, b) => {
    const startA = a?.period?.start || '';
    const startB = b?.period?.start || '';
    if (startA !== startB) return startA < startB ? 1 : -1;
    return String(a?.id || '').localeCompare(String(b?.id || ''));
};

/**
 * The patient's current PROGRAM encounter and the episode of care it belongs to.
 *
 * A schedule is stamped with the episode, not the encounter, so a patient
 * stepping detox -> residential inside one episode keeps the same episode id and
 * every schedule stays live with no re-confirmation and no gap in doses.
 *
 * @param {import('vue').Ref<string>|string} patientId
 * @returns {{episode: import('vue').Ref<object|null>, programEncounter: import('vue').Ref<object|null>, loading: import('vue').Ref<boolean>, error: import('vue').Ref<Error|null>}}
 */
export function useCurrentEpisode(patientId) {
    const episode = ref(null);
    const programEncounter = ref(null);
    const loading = ref(false);
    const error = ref(null);

    // Discards a response whose patient is no longer the one being asked about.
    let requestToken = 0;

    const load = async (id) => {
        const token = ++requestToken;
        episode.value = null;
        programEncounter.value = null;
        error.value = null;

        if (!id) {
            loading.value = false;
            return;
        }

        loading.value = true;
        try {
            const response = await $httpFhirApi.get(
                `Encounter?subject=${encodeURIComponent(id)}&type:text=PROGRAM&status=${OPEN_STATUSES.join(',')}&appointment:missing=true&_include=episode-of-care&_count=${ENCOUNTER_COUNT}`,
            );
            if (token !== requestToken) return;

            const resources = Array.isArray(response?.data) ? response.data : [];
            const encounters = resources.filter((item) => item?.resourceType === 'Encounter' && OPEN_STATUSES.includes(item.status) && item.type?.some((type) => type?.text === 'PROGRAM'));

            const encounter = encounters.sort(byMostRecentThenId)[0] || null;
            programEncounter.value = encounter;

            const episodeId = encounter?.episodeOfCare?.find((reference) => reference?.id)?.id;
            episode.value = episodeId ? resources.find((item) => item?.resourceType === 'EpisodeOfCare' && item.id === episodeId) || null : null;
        } catch (err) {
            if (token !== requestToken) return;
            error.value = err;
            $log.error('useCurrentEpisode: failed to load the program encounter', id, err?.message, err?.stack);
        } finally {
            if (token === requestToken) loading.value = false;
        }
    };

    watch(() => unref(patientId), load, { immediate: true });

    return { episode, programEncounter, loading, error };
}

export default useCurrentEpisode;
